import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search,
  Sparkles,
  X,
  Star,
  Download,
  Eye,
  Calendar,
  LayoutGrid,
  List,
  ChevronDown,
  Check,
  SearchX,
} from "lucide-react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import Header from "../Components/Header";
import AnimatedCard, { MotionItem } from "../Components/AnimatedCard";

const EASE = [0.22, 1, 0.36, 1];

// Same fade/slide-in as MotionItem, but also staggers its own children -
// used for the template grids so each card animates in turn
const gridContainer = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE, staggerChildren: 0.08 },
  },
};

/* -------------------------------------------------------------------------- */
/*  Config                                                                     */
/* -------------------------------------------------------------------------- */

// Canonical display names. Keys are lower-cased tags, so "GMAIL", "Gmail" and
// "gmail" all collapse into one filter option.
const TOOL_LABELS = {
  n8n: "n8n",
  tiktok: "TikTok",
  "vidéo": "Vidéo",
  "vidéos": "Vidéo",
  airtable: "Airtable",
  gemini: "Gemini",
  apify: "Apify",
  linkedin: "LinkedIn",
  sheets: "Google Sheets",
  sheet: "Google Sheets",
  scraping: "Scraping",
  google: "Google",
  rag: "RAG",
  documents: "Documents",
  prospection: "Prospection",
  apollo: "Apollo",
  hashtags: "Hashtags",
  heygen: "HeyGen",
  perplexity: "Perplexity",
  blotato: "Blotato",
  recherche: "Recherche",
  coldemail: "Cold email",
  gmail: "Gmail",
  instagram: "Instagram",
  facebook: "Facebook",
  "créationcontenus": "Création de contenu",
  mcp: "MCP",
  slack: "Slack",
  "google calendar": "Google Calendar",
  calendar: "Google Calendar",
  seo: "SEO",
  audit: "Audit",
  "site web": "Site web",
  rss: "RSS",
  articles: "Articles",
  news: "Actualités",
  "actualités": "Actualités",
  ia: "IA",
  "google maps": "Google Maps",
  agentia: "Agent IA",
  "agent ia": "Agent IA",
  brave: "Brave",
  firecrawl: "Firecrawl",
  puppeteer: "Puppeteer",
  notifications: "Notifications",
  erreur: "Erreur",
  telegram: "Telegram",
  outlook: "Outlook",
  image: "Image",
  "nano banana": "Nano Banana",
  googledrive: "Google Drive",
  sauvegarde: "Sauvegarde",
  make: "Make",
};

// First matching rule wins. Anything else falls back to "Automatisation".
const CATEGORY_RULES = [
  { name: "Prospection", keys: ["apollo", "prospection", "coldemail", "google maps", "linkedin"] },
  { name: "Vidéo IA", keys: ["tiktok", "heygen", "vidéo", "vidéos", "hashtags"] },
  { name: "RAG et documents", keys: ["rag", "documents"] },
  { name: "SEO", keys: ["seo", "audit", "site web"] },
  { name: "Agents MCP", keys: ["mcp", "agentia", "agent ia"] },
  {
    name: "Contenu social",
    keys: ["instagram", "facebook", "rss", "articles", "créationcontenus", "news", "actualités", "blotato"],
  },
];
const FALLBACK_CATEGORY = "Automatisation";
const CATEGORY_ORDER = [...CATEGORY_RULES.map((r) => r.name), FALLBACK_CATEGORY];

const SORT_OPTIONS = [
  { value: "recommended", label: "Recommandés" },
  { value: "recent", label: "Plus récents" },
  { value: "az", label: "A à Z" },
];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const keyOf = (tag) => String(tag).trim().toLowerCase();

const labelOf = (tag) => {
  const k = keyOf(tag);
  if (TOOL_LABELS[k]) return TOOL_LABELS[k];
  const t = String(tag).trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const categoryOf = (tags) => {
  const keys = tags.map(keyOf);
  const rule = CATEGORY_RULES.find((r) => r.keys.some((k) => keys.includes(k)));
  return rule ? rule.name : FALLBACK_CATEGORY;
};

// DOMParser never executes scripts or loads images, unlike innerHTML on a live node.
const stripHtml = (html = "") =>
  new DOMParser().parseFromString(html, "text/html").body.textContent?.trim() || "";

// Removes the repeated "ORRA –" prefix (and any leading emoji) from titles.
const cleanTitle = (title = "") =>
  title.replace(/^[^\p{L}\p{N}]*orra\s*[–—-]\s*/iu, "").trim() || title;

const toDate = (value) => {
  try {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const formatDate = (date) =>
  date
    ? date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

const hueOf = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
};

const openWorkflow = (url, event) => {
  event?.stopPropagation();
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

/* -------------------------------------------------------------------------- */
/*  Small components                                                           */
/* -------------------------------------------------------------------------- */

const ToolAvatar = ({ label, size = 28 }) => {
  const hue = hueOf(label);
  return (
    <span
      title={label}
      className="inline-flex items-center justify-center rounded-full ring-2 ring-[#0d1330] text-[10px] font-semibold select-none"
      style={{
        width: size,
        height: size,
        background: `hsl(${hue} 55% 22%)`,
        color: `hsl(${hue} 90% 82%)`,
      }}
    >
      {label.slice(0, 2)}
    </span>
  );
};

const ToolStack = ({ tools, max = 4 }) => (
  <div className="flex items-center">
    <div className="flex -space-x-2">
      {tools.slice(0, max).map((t) => (
        <ToolAvatar key={t} label={t} />
      ))}
    </div>
    {tools.length > max && (
      <span className="ml-2 text-xs text-slate-400">+{tools.length - max}</span>
    )}
  </div>
);

const CategoryPill = ({ children }) => (
  <span className="rounded-full bg-blue-500/15 px-2.5 py-0.5 text-xs font-medium text-blue-300">
    {children}
  </span>
);

const TemplateCard = ({ template, layout, featured, onPreview }) => {
  const isList = layout === "list";

  return (
    <article
      className={`group relative flex rounded-2xl border bg-white/[0.03] transition-colors duration-200 hover:border-white/25 hover:bg-white/[0.05] ${
        featured ? "border-blue-400/40" : "border-white/10"
      } ${isList ? "flex-col gap-4 p-4 sm:flex-row sm:items-center" : "flex-col gap-4 p-5"}`}
    >
      {featured && (
        <span className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#113BE1] to-[#4268FF] px-2.5 py-0.5 text-[11px] font-medium text-white">
          <Star className="h-3 w-3 fill-current" aria-hidden="true" />
          Recommandé
        </span>
      )}

      <div className={isList ? "flex-1 min-w-0" : "flex flex-1 flex-col gap-3"}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <ToolStack tools={template.tools} />
          <CategoryPill>{template.category}</CategoryPill>
        </div>

        <button
          type="button"
          onClick={() => onPreview(template)}
          className="text-left focus:outline-none focus-visible:underline"
        >
          <h3 className="line-clamp-2 text-base font-semibold leading-snug text-white">
            {template.cleanTitle}
          </h3>
        </button>

        <p
          className={`mt-2 text-sm leading-relaxed text-slate-400 ${
            isList ? "line-clamp-2" : "line-clamp-3"
          }`}
        >
          {template.plainDescription}
        </p>

        {(template.duration || template.level) && (
          <p className="mt-3 text-xs text-slate-500">
            {[template.duration, template.level].filter(Boolean).join("  |  ")}
          </p>
        )}
      </div>

      <div className={`flex gap-2 ${isList ? "sm:w-64 sm:shrink-0" : ""}`}>
        <button
          type="button"
          onClick={(e) => openWorkflow(template.jsonUrl, e)}
          disabled={!template.jsonUrl}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#113BE1] to-[#4268FF] px-3 py-2 text-sm font-medium text-white transition-[filter] hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          Utiliser
        </button>
        <button
          type="button"
          onClick={() => onPreview(template)}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <Eye className="h-4 w-4" aria-hidden="true" />
          Aperçu
        </button>
      </div>
    </article>
  );
};

const SkeletonCard = () => (
  <div className="animate-pulse space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
    <div className="flex justify-between">
      <div className="h-7 w-24 rounded-full bg-white/10" />
      <div className="h-5 w-20 rounded-full bg-white/10" />
    </div>
    <div className="h-5 w-3/4 rounded bg-white/10" />
    <div className="space-y-2">
      <div className="h-3 w-full rounded bg-white/10" />
      <div className="h-3 w-5/6 rounded bg-white/10" />
    </div>
    <div className="h-9 w-full rounded-lg bg-white/10" />
  </div>
);

const ToolFilter = ({ options, selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visible = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-sm text-slate-200 transition-colors hover:bg-white/[0.07] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        Outils
        {selected.length > 0 && (
          <span className="rounded-full bg-blue-600 px-1.5 text-xs font-medium text-white">
            {selected.length}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-white/10 bg-[#0f1536] p-2 shadow-2xl sm:left-0 sm:right-auto">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Chercher un outil"
            className="mb-2 h-9 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <ul
            role="listbox"
            aria-multiselectable="true"
            className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-[#4268ff] scrollbar-track-[#113be1]/0 scrollbar-thumb-rounded-full scrollbar-corner-[#113be1]/0"
          >
            {visible.length === 0 && (
              <li className="px-3 py-4 text-center text-sm text-slate-500">Aucun outil trouvé.</li>
            )}
            {visible.map((o) => {
              const active = selected.includes(o.key);
              return (
                <li key={o.key} role="option" aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => onToggle(o.key)}
                    className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm text-slate-200 hover:bg-white/10 focus:outline-none focus-visible:bg-white/10"
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded border ${
                        active ? "border-blue-500 bg-blue-600" : "border-white/25"
                      }`}
                    >
                      {active && <Check className="h-3 w-3 text-white" aria-hidden="true" />}
                    </span>
                    <span className="flex-1 truncate">{o.label}</span>
                    <span className="text-xs text-slate-500">{o.count}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="mt-2 w-full rounded-md border-t border-white/10 px-3 pt-2 text-left text-xs text-slate-400 hover:text-white"
            >
              Effacer la sélection
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PreviewDrawer = ({ template, onClose }) => {
  useEffect(() => {
    if (!template) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [template, onClose]);

  return (
    <AnimatePresence>
      {template && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={template.cleanTitle}>
          <motion.div
            className="absolute inset-0 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="relative flex h-full w-full max-w-xl flex-col border-l border-white/10 bg-[#0b1030]"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
          >
            <div className="flex items-start justify-between gap-4 border-b border-white/10 p-6">
              <div className="min-w-0 text-left">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <CategoryPill>{template.category}</CategoryPill>
                  {template.featured && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                      <Star className="h-3 w-3 fill-current" aria-hidden="true" />
                      Recommandé
                    </span>
                  )}
                </div>
                <h2 className="text-xl font-semibold leading-snug text-white">{template.cleanTitle}</h2>
                {template.dateLabel && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-400">
                    <Calendar className="h-4 w-4" aria-hidden="true" />
                    Ajouté le {template.dateLabel}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Fermer l'aperçu"
                className="rounded-full p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-8 overflow-y-auto p-6 text-left scrollbar-thin scrollbar-thumb-[#4268ff] scrollbar-track-[#113be1]/0 scrollbar-thumb-rounded-full scrollbar-corner-[#113be1]/0">
              <section>
                <h3 className="mb-3 text-sm font-medium text-slate-300">Description</h3>
                <div
                  className="prose prose-invert max-w-none text-sm leading-relaxed text-slate-300"
                  dangerouslySetInnerHTML={{ __html: template.description }}
                />
              </section>

              <section>
                <h3 className="mb-3 text-sm font-medium text-slate-300">Outils utilisés</h3>
                <div className="flex flex-wrap gap-2">
                  {template.tools.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 py-1 pl-1 pr-3 text-sm text-slate-200"
                    >
                      <ToolAvatar label={t} size={22} />
                      {t}
                    </span>
                  ))}
                </div>
              </section>
            </div>

            <div className="border-t border-white/10 p-6">
              <button
                type="button"
                onClick={(e) => openWorkflow(template.jsonUrl, e)}
                disabled={!template.jsonUrl}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#113BE1] to-[#4268FF] px-4 py-3 text-sm font-medium text-white transition-[filter] hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Utiliser ce modèle
              </button>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const AIAgentTemplates = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [rawTemplates, setRawTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("Tous");
  const [selectedTools, setSelectedTools] = useState([]);
  const [sortBy, setSortBy] = useState("recommended");
  const [layout, setLayout] = useState("grid");
  const [previewed, setPreviewed] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "agentTemplates"),
      (snapshot) => {
        setRawTemplates(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setIsLoading(false);
      },
      (err) => {
        console.error("agentTemplates listener error:", err);
        setError("Impossible de charger les templates. Réessayez dans un instant.");
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Normalise every document once, so rendering and filtering stay cheap.
  const templates = useMemo(
    () =>
      rawTemplates.map((t) => {
        const tags = Array.isArray(t.tags) ? t.tags : [];
        const toolMap = new Map();
        tags.forEach((tag) => {
          const k = keyOf(TOOL_LABELS[keyOf(tag)] || tag);
          if (!toolMap.has(k)) toolMap.set(k, labelOf(tag));
        });
        const date = toDate(t.createdAt);
        return {
          ...t,
          description: t.description || "",
          tools: [...toolMap.values()],
          toolKeys: [...toolMap.keys()],
          category: categoryOf(tags),
          cleanTitle: cleanTitle(t.title || "Sans titre"),
          plainDescription: stripHtml(t.description),
          timestamp: date ? date.getTime() : 0,
          dateLabel: formatDate(date),
        };
      }),
    [rawTemplates]
  );

  const categories = useMemo(() => {
    const counts = templates.reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + 1;
      return acc;
    }, {});
    return [
      { name: "Tous", count: templates.length },
      ...CATEGORY_ORDER.filter((c) => counts[c]).map((c) => ({ name: c, count: counts[c] })),
    ];
  }, [templates]);

  const toolOptions = useMemo(() => {
    const map = new Map();
    templates.forEach((t) =>
      t.tools.forEach((label, i) => {
        const key = t.toolKeys[i];
        const entry = map.get(key) || { key, label, count: 0 };
        entry.count += 1;
        map.set(key, entry);
      })
    );
    return [...map.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "fr"));
  }, [templates]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const list = templates.filter((t) => {
      if (category !== "Tous" && t.category !== category) return false;
      if (selectedTools.length && !selectedTools.every((k) => t.toolKeys.includes(k))) return false;
      if (q && !`${t.cleanTitle} ${t.plainDescription} ${t.tools.join(" ")}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });

    const sorters = {
      recommended: (a, b) => Number(!!b.featured) - Number(!!a.featured) || b.timestamp - a.timestamp,
      recent: (a, b) => b.timestamp - a.timestamp,
      az: (a, b) => a.cleanTitle.localeCompare(b.cleanTitle, "fr"),
    };
    return [...list].sort(sorters[sortBy]);
  }, [templates, searchTerm, category, selectedTools, sortBy]);

  const hasActiveFilters = searchTerm || category !== "Tous" || selectedTools.length > 0;
  const showFeaturedRow = !hasActiveFilters && sortBy === "recommended";
  const featuredRow = showFeaturedRow ? filtered.filter((t) => t.featured).slice(0, 3) : [];
  const featuredIds = new Set(featuredRow.map((t) => t.id));
  const rest = filtered.filter((t) => !featuredIds.has(t.id));

  const toggleTool = useCallback(
    (key) => setSelectedTools((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])),
    []
  );

  const resetFilters = () => {
    setSearchTerm("");
    setCategory("Tous");
    setSelectedTools([]);
  };

  const closePreview = useCallback(() => setPreviewed(null), []);

  const gridClass =
    layout === "grid"
      ? "grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
      : "flex flex-col gap-3";

  return (
    <div className="min-h-screen">
      <div className="flex flex-col">
        <Header PageName={"Mes Agents IA"} isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
      </div>

      <AnimatedCard
        tilt={false}
        effects="light"
        className="flex flex-col gap-8 rounded-[15px] border border-borderColor bg-bgColor p-6"
      >
        {/* Header */}
        <MotionItem className="text-left">
          <h1 className="flex items-center gap-3 text-3xl font-bold text-white">
            <Sparkles className="h-7 w-7 text-blue-400" aria-hidden="true" />
            Templates d'agents IA
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Des workflows n8n et Make prêts à importer pour automatiser votre prospection, vos contenus et vos
            veilles.
          </p>
        </MotionItem>

        {/* Toolbar */}
        <MotionItem className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                aria-hidden="true"
              />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un template, un outil…"
                aria-label="Rechercher un template"
                className="h-10 w-full rounded-lg border border-white/10 bg-white/[0.03] pl-10 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-blue-400/60 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>

            <div className="flex items-center gap-3">
              <ToolFilter
                options={toolOptions}
                selected={selectedTools}
                onToggle={toggleTool}
                onClear={() => setSelectedTools([])}
              />

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Trier les templates"
                className="h-10 rounded-lg border border-white/10 bg-[#0f1536] px-3 text-sm text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <div className="flex h-10 items-center rounded-lg border border-white/10 p-1" role="group" aria-label="Affichage">
                {[
                  { id: "grid", Icon: LayoutGrid, label: "Affichage en grille" },
                  { id: "list", Icon: List, label: "Affichage en liste" },
                ].map(({ id, Icon, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setLayout(id)}
                    aria-label={label}
                    aria-pressed={layout === id}
                    className={`flex h-full w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                      layout === id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Category tabs */}
          <div
            className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-thin scrollbar-thumb-[#4268ff] scrollbar-track-[#113be1]/0 scrollbar-thumb-rounded-full scrollbar-corner-[#113be1]/0"
            role="tablist"
            aria-label="Catégories"
          >
            {categories.map((c) => {
              const active = category === c.name;
              return (
                <button
                  key={c.name}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCategory(c.name)}
                  className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    active
                      ? "border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                      : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
                  }`}
                >
                  {c.name}
                  <span className={`text-xs ${active ? "text-blue-100" : "text-slate-500"}`}>{c.count}</span>
                </button>
              );
            })}
          </div>

          {/* Active tool chips */}
          {selectedTools.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {selectedTools.map((k) => {
                const opt = toolOptions.find((o) => o.key === k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleTool(k)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/10 py-1 pl-3 pr-2 text-xs text-slate-200 hover:bg-white/15"
                  >
                    {opt?.label || k}
                    <X className="h-3 w-3" aria-label={`Retirer ${opt?.label || k}`} />
                  </button>
                );
              })}
            </div>
          )}
        </MotionItem>

        {/* Content */}
        {error ? (
          <MotionItem className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center text-sm text-red-200">
            <div role="alert">{error}</div>
          </MotionItem>
        ) : isLoading ? (
          <motion.div
            variants={gridContainer}
            className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
            aria-busy="true"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <MotionItem key={i}>
                <SkeletonCard />
              </MotionItem>
            ))}
          </motion.div>
        ) : filtered.length === 0 ? (
          <MotionItem className="flex flex-col items-center gap-3 py-16 text-center">
            <SearchX className="h-10 w-10 text-slate-500" aria-hidden="true" />
            <h2 className="text-lg font-medium text-white">Aucun template trouvé</h2>
            <p className="max-w-sm text-sm text-slate-400">
              Essayez un autre mot-clé ou retirez un filtre pour voir plus de résultats.
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-2 rounded-lg border border-white/15 px-4 py-2 text-sm text-slate-200 hover:bg-white/10"
              >
                Réinitialiser les filtres
              </button>
            )}
          </MotionItem>
        ) : (
          <MotionItem className="flex flex-col gap-10">
            {featuredRow.length > 0 && (
              <section aria-labelledby="featured-title">
                <h2 id="featured-title" className="mb-5 text-left text-lg font-semibold text-white">
                  Recommandés pour vous
                </h2>
                <motion.div
                  variants={gridContainer}
                  className={gridClass.replace("xl:grid-cols-3", "lg:grid-cols-3")}
                >
                  {featuredRow.map((t) => (
                    <MotionItem key={t.id}>
                      <TemplateCard template={t} layout={layout} featured onPreview={setPreviewed} />
                    </MotionItem>
                  ))}
                </motion.div>
              </section>
            )}

            {rest.length > 0 && (
              <section aria-labelledby="all-title">
                <div className="mb-5 flex items-baseline justify-between">
                  <h2 id="all-title" className="text-lg font-semibold text-white">
                    {featuredRow.length > 0 ? "Tous les templates" : "Résultats"}
                  </h2>
                  <span className="text-sm text-slate-500" aria-live="polite">
                    {filtered.length} template{filtered.length > 1 ? "s" : ""}
                  </span>
                </div>
                <motion.div variants={gridContainer} className={gridClass}>
                  {rest.map((t) => (
                    <MotionItem key={t.id}>
                      <TemplateCard template={t} layout={layout} onPreview={setPreviewed} />
                    </MotionItem>
                  ))}
                </motion.div>
              </section>
            )}
          </MotionItem>
        )}
      </AnimatedCard>

      <PreviewDrawer template={previewed} onClose={closePreview} />
    </div>
  );
};

export default AIAgentTemplates;